import { _callBackend } from '@/_api/api';
import { processDocument, fileUpload } from '@/_api/client-upload';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { DirectoryType, DocumentItem } from '@/_types/models/documents';
import type { DocumentDownload } from '@/_types/documents';
import JSZip from 'jszip';
import download from 'downloadjs';

export const uploadToStorage = async ({
  companyId,
  description = '',
  documentType,
  file,
  markedAsOthers,
  path,
  questionnaireId,
  requiresSplitting,
  selectedOptions,
  shouldSkipMatching,
  tags,
  newName,
  token,
}: APIRequest<{
  companyId: number;
  description?: string;
  documentType?: string;
  file: File;
  markedAsOthers?: boolean;
  path: string;
  questionnaireId?: number;
  requiresSplitting?: boolean;
  selectedOptions?: Array<string>;
  shouldSkipMatching?: boolean;
  tags?: Array<string>;
  newName?: string;
}>): APIResponsePromise<{ is_duplicate: boolean; document_name: string }> => {
  // Get a temporary upload link
  const { upload_link, id } = await _callBackend(
    `/integrations/get_temporary_upload_link?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

  // Upload file
  const url = new URL(upload_link);
  const isDropbox: boolean = url.hostname.includes('dropbox');
  await fileUpload(upload_link, isDropbox, file);

  // Process uploaded document
  const processed = await processDocument({
    companyId,
    description,
    documentName: newName || file.name,
    documentType,
    id,
    markedAsOthers,
    path: path || '',
    questionnaireId,
    requiresSplitting,
    selectedOptions,
    shouldSkipMatching,
    tags,
    token,
  });

  return processed;
};

// --------------------------------
// Documents

export const getDocuments = async ({
  companyId,
  path,
  token,
  showWithDeleted,
}: APIRequest<{
  companyId: number;
  path: string;
  showWithDeleted?: boolean;
}>): APIResponsePromise<{ documents: Array<DocumentItem | DirectoryType> }> => {
  const resp = await _callBackend(
    '/integrations/get_documents',
    'POST',
    {
      company_id: companyId,
      path,
      show_with_deleted: showWithDeleted,
    },
    token,
  );

  const documents: Array<unknown> = Array.isArray(resp?.documents)
    ? resp.documents
    : [];

  return { documents } as { documents: Array<DocumentItem | DirectoryType> };
};

// TODO(API): @chris should be a recursive search
export const searchDocument = async ({
  companyId,
  searchTerm,
  token,
}: APIRequest<{
  companyId: number;
  searchTerm: string;
}>): APIResponsePromise<{
  documents: Array<DocumentItem>;
}> => {
  return await _callBackend(
    '/integrations/search_documents',
    'POST',
    {
      company_id: companyId,
      path: '',
      search_term: searchTerm,
    },
    token,
  );
};

export const createFolderInS3 = async ({
  companyId,
  path,
  token,
}: APIRequest<{
  companyId: number;
  path: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/create_folder',
    'POST',
    { company_id: companyId, path: path || '' },
    token,
  );

export const renameItem = async ({
  companyId,
  itemName,
  newName,
  path,
  extractedDocumentId,
  token,
}: APIRequest<{
  companyId: number;
  itemName: string;
  newName: string;
  path: string;
  extractedDocumentId?: number | null;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/rename_item',
    'POST',
    {
      company_id: companyId,
      item_name: itemName,
      new_name: newName,
      path: path || '',
      extracted_document_id: extractedDocumentId,
    },
    token,
  );

export const updateDescription = async ({
  companyId,
  description,
  extractedDocumentId,
  token,
}: APIRequest<{
  companyId: number;
  extractedDocumentId: number;
  description: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${extractedDocumentId}/update_description`,
    'POST',
    {
      company_id: companyId,
      description,
    },
    token,
  );

export const addDocumentTag = async ({
  tag,
  token,
}: APIRequest<{
  tag: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/firms/add_document_tag?name=${encodeURIComponent(tag)}`,
    'GET',
    {},
    token,
  );

export const deleteDocumentTag = async ({
  tag,
  token,
}: APIRequest<{
  tag: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/firms/remove_document_tag?name=${encodeURIComponent(tag)}`,
    'GET',
    {},
    token,
  );

export const updateDocumentTag = async ({
  companyId,
  extractedDocumentId,
  tags,
  token,
}: APIRequest<{
  companyId: number;
  extractedDocumentId: number;
  tags: Array<string>;
}>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${extractedDocumentId}/update_tags`,
    'POST',
    {
      company_id: companyId,
      tags,
    },
    token,
  );

export const updateDocumentType = async ({
  companyId,
  extractedDocumentId,
  documentType,
  token,
}: APIRequest<{
  companyId: number;
  extractedDocumentId: number;
  documentType: string;
}>): Promise<Response> => {
  const { updateDocumentType } = await import('@/_api/admin-review-and-export');

  return updateDocumentType({
    token,
    companyId,
    extractedDocumentId,
    documentType,
  });
};

export const moveItem = async ({
  companyId,
  deleteOldPath,
  newCompanyId,
  newPath,
  oldPath,
  isFolder,
  extractedDocumentId,
  token,
}: APIRequest<{
  companyId: number;
  deleteOldPath?: boolean;
  newCompanyId?: number;
  newPath: string;
  oldPath: string;
  isFolder: boolean;
  extractedDocumentId?: number | null;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/move_item',
    'POST',
    {
      company_id: companyId,
      delete_old_path: deleteOldPath || false,
      is_folder: isFolder,
      new_company_id: newCompanyId,
      new_path: newPath,
      old_path: oldPath,
      extracted_document_id: extractedDocumentId,
    },
    token,
  );

export const downloadDocument = async ({
  companyId,
  link,
  isDownload,
  includeAnnotations,
  extractedDocumentId,
  token,
}: APIRequest<{
  companyId: number;
  link: string;
  isDownload?: boolean;
  includeAnnotations?: boolean;
  extractedDocumentId?: number;
}>): APIResponsePromise<{ document: DocumentDownload }> =>
  await _callBackend(
    '/integrations/download_document',
    'POST',
    {
      company_id: companyId,
      link: link || '',
      download: isDownload,
      include_annotations: includeAnnotations,
      extracted_document_id: extractedDocumentId,
    },
    token,
  );

export const downloadAttachment = async ({
  link,
  token,
}: APIRequest<{
  link: string;
}>): APIResponsePromise<{ document: DocumentDownload }> =>
  await _callBackend(
    '/conversations/download_attachment',
    'POST',
    {
      link,
    },
    token,
  );

export const downloadUserDocument = async ({
  link,
  token,
}: APIRequest<{
  link: string;
}>): APIResponsePromise<{ document: DocumentDownload }> =>
  await _callBackend(
    '/users/download_user_document',
    'POST',
    {
      link: link || '',
    },
    token,
  );

export const downloadFolder = async ({
  companyId,
  link,
  name,
  token,
}: APIRequest<{
  name: string;
  companyId: number;
  link: string;
}>): APIResponsePromise => {
  const response = await _callBackend(
    '/integrations/download_folder',
    'POST',
    {
      company_id: companyId,
      link,
    },
    token,
    { returnResponseAsIs: true },
  );

  const blob = await response.blob();
  const url = globalThis?.URL?.createObjectURL(blob);
  const downloadLink: HTMLAnchorElement = document.createElement('a');

  downloadLink.href = url;
  downloadLink.setAttribute('download', name + '.zip');
  document.body.appendChild(downloadLink);
  downloadLink.click();

  return response;
};

export const convertToPdfsAndDownloadFolder = async ({
  companyId,
  link,
  token,
}: APIRequest<{
  companyId: number;
  link: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/convert_to_pdfs_and_download_folder',
    'POST',
    {
      company_id: companyId,
      link,
    },
    token,
    { returnResponseAsIs: true },
  );

export const deleteDocument = async ({
  companyId,
  documentName,
  isFolder,
  path,
  token,
}: APIRequest<{
  companyId: number;
  documentName: string;
  isFolder: boolean;
  path: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/delete_document',
    'POST',
    {
      company_id: companyId,
      document_name: documentName,
      is_folder: isFolder,
      path,
    },
    token,
  );

export const deleteExtractedDocument = async ({
  id,
  token,
}: APIRequest<{
  id: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${id}/delete_extracted_document`,
    'GET',
    null,
    token,
  );

export const downloadMultipleDocuments = async ({
  companyId,
  documents,
  convertImageToPdf,
  token,
}: APIRequest<{
  companyId: number;
  documents: Array<DocumentItem>;
  convertImageToPdf: boolean;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/download_multiple_documents',
    'POST',
    {
      company_id: companyId,
      documents,
      convert_image_to_pdf: convertImageToPdf,
    },
    token,
  );

export const downloadMultipleDocumentsFromBrowser = async ({
  companyId,
  documents,
  token,
}: APIRequest<{
  companyId: number;
  documents: Array<DocumentItem>;
}>) => {
  const zip = new JSZip();

  await Promise.all(
    documents
      .filter((doc) => doc.type === 'file')
      .map((doc) => {
        return downloadDocumentAsBuffer({
          companyId,
          documentLink: doc.link,
          token,
        }).then((buffer) => {
          zip.file(doc.name, buffer);
        });
      }),
  );

  await zip.generateAsync({ type: 'blob' }).then(function (content) {
    download(content, 'files.zip', 'application/zip');
  });
};

export const downloadDocumentAsBuffer = async ({
  companyId,
  documentLink,
  token,
}: APIRequest<{
  companyId: number;
  documentLink: string;
}>) => {
  const response = await downloadDocument({
    companyId,
    link: documentLink,
    token,
  });

  const url = response.document.data.link;

  return fetch(url).then((res) => res.arrayBuffer());
};

export const attachToQuestionnaireInBulk = async ({
  questionnaireId,
  companyId,
  extractedDocumentIds,
  token,
}: APIRequest<{
  questionnaireId: number;
  companyId: number;
  extractedDocumentIds: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/questionnaires/${questionnaireId}/attach_to_questionnaire`,
    'POST',
    {
      company_id: companyId,
      extracted_document_ids: extractedDocumentIds,
    },
    token,
  );
